import {autoRegister, resolve} from "../../container";
import {type ChatResponse, GptChatService} from "./gptChatService";
import {SessionStorage} from "../../common/clientStorage";
import {Dictionary} from "../../page/elements/dictionary";
import {schedule} from "../../common/utils/promises";
import {LanguagesService} from "../../common/languages";
import {sanitizeHTML} from "../../common/sanitizer";
import {TrackingEventName} from "../../tracking/types";

const JOB_URL_MARKDOWN_PATTERN = /\[([^[\]]+)]\((https?:\/\/[^\s)]+)\)/g; //example: [click me](https://www.example.com) <- label and link are in a group each without parenthesis
const JOB_HIGHLIGHT_MARKDOWN_PATTERN = /(\*{2}).+?(\*{2})/g; //example: **I am bold** <- highlighted text is in a group
const JOB_HEADLINE_MARKDOWN_PATTERN = /^#+ /gm;
const EMAIL_PATTERN = /([a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,64}\.[a-zA-Z]{2,63})/g;

export type Message = {
    content: string;
    role: Role;
    assistantMessageId?: string;
}

export enum Role {
    ASSISTANT = "assistant",
    USER = "user",
}

export enum MessageSource {
    WELCOME = "welcome",
    SESSION_STORAGE = "session-storage",
    LIVE = "live"
}

const processSanitize: MessageProcessor = (message: string) => {
    return sanitizeHTML(message);
};

const processMarkdownLinks: MessageProcessor = (message: string) => {
    const markdownRefs = message.matchAll(JOB_URL_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownRef of markdownRefs) {
        const clickLabel = markdownRef[1];
        const plainRef = markdownRef[2];

        const link = `<a href="${plainRef}" target="_blank" class="chat-message-link link"
            data-tracking-event-name="${TrackingEventName.TEXTLINK_INTERACT}"
            data-tracking-payload-text="${clickLabel}"
            data-tracking-payload-link="${plainRef}"
        >${clickLabel}</a>`;
        processedMessage = processedMessage.replace(markdownRef[0], link);
    }
    return processedMessage;
};

const processATags: MessageProcessor = (message: string) => {
    const messageElement = document.createElement("div");
    messageElement.innerHTML = message;

    const links = messageElement.querySelectorAll("a");
    links.forEach((link) => {
        link.classList.add("chat-message-link", "link");
        link.target = "_blank";
    });

    return messageElement.innerHTML;
};

const processMarkdownHighlighting: MessageProcessor = (message: string) => {
    const markdownHighlights = message.matchAll(JOB_HIGHLIGHT_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownHighlight of markdownHighlights) {
        processedMessage = processedMessage.replace(markdownHighlight[1], "<b>");
        processedMessage = processedMessage.replace(markdownHighlight[2], "</b>");
    }
    return processedMessage;
};

const processMarkdownHeadline: MessageProcessor = (message: string) => {
    const markdownHeadlines = message.matchAll(JOB_HEADLINE_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownHeadline of markdownHeadlines) {
        processedMessage = processedMessage.replace(markdownHeadline[0], "");
    }
    return processedMessage;
};

const processPlaintextEmails: MessageProcessor = (message: string) => {
    return message.replaceAll(EMAIL_PATTERN, (match) => {
        const isAlreadyLink = message.includes(`href="mailto:${match}"`);
        if (isAlreadyLink) {
            return match;
        }
        return `<a href="mailto:${match}" class="chat-message-link link"
            data-tracking-event-name="${TrackingEventName.TEXTLINK_INTERACT}"
            data-tracking-payload-text="${match}"
            data-tracking-payload-link="mailto:${match}"
        >${match}</a>`;
    });
};

class CompoundMessageProcessor {
    private processors: MessageProcessor[] = [];

    public addProcessor(processor: MessageProcessor): this {
        this.processors.push(processor);
        return this;
    }

    public process(message: string): string {
        let processedMessage = message;
        this.processors.forEach((processorCallback) => {
            processedMessage = processorCallback(processedMessage);
        });
        return processedMessage;
    }
}

type StoredChatSession = {
    chatId: string;
    assistantMessageId?: string;
}

type MessageProcessor = (message: string) => string;

type NewMessagesCallback = (messages: Message[], source: MessageSource) => void;

@autoRegister()
export class GptChatAppFactory {

    public constructor(
        private gptChatService: GptChatService = resolve(GptChatService),
        private sessionStorage: SessionStorage = resolve(SessionStorage),
        private languages: LanguagesService = resolve(LanguagesService)
    ) {
    }

    public create(configId: string, dictionary: Dictionary): GptChatApp {
        return new GptChatApp(this.gptChatService, this.sessionStorage, this.languages, configId, dictionary);
    }
}

export class GptChatApp {

    private messageProcessor = new CompoundMessageProcessor()
        .addProcessor(processSanitize)
        .addProcessor(processPlaintextEmails)
        .addProcessor(processMarkdownLinks)
        .addProcessor(processATags)
        .addProcessor(processMarkdownHighlighting)
        .addProcessor(processMarkdownHeadline);

    private chatSessionStorageKey: string;
    private chatCommunicationStorageKey: string;

    private newMessagesCallback: NewMessagesCallback[] = [];

    public constructor(
        private gptChatService: GptChatService,
        private sessionStorage: SessionStorage,
        private languages: LanguagesService,
        private configId: string,
        private dictionary: Dictionary
    ) {
        this.chatSessionStorageKey = "career-gpt-session-" + this.configId + "-" + this.languages.activeLanguageId();
        this.chatCommunicationStorageKey = "career-gpt-communication-" + this.configId + "-" + this.languages.activeLanguageId();
    }

    public onNewMessages(callback: NewMessagesCallback): void {
        this.newMessagesCallback.push(callback);
    }

    public async chat(userInput: string): Promise<void> {
        let messages = this.saveMessage({content: userInput, role: Role.USER});
        this.fireNewMessages(messages, MessageSource.LIVE);

        const response = await this.post(userInput);

        messages = this.saveMessage({
            content: this.postProcessMessage(response.message, response.responseType),
            role: Role.ASSISTANT,
            assistantMessageId: response.assistantMessageId
        });
        this.fireNewMessages(messages, MessageSource.LIVE);
    }

    public async rateAssistantResponse(ratingPositive: boolean): Promise<void> {
        const session = this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey);
        if (session?.assistantMessageId) {
            await this.gptChatService.rateAssistantResponse(session.chatId, session.assistantMessageId, ratingPositive);
        }
    }

    public chatId(): string | undefined {
        return this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey)?.chatId;
    }

    private async post(userInput: string): Promise<ChatResponse> {
        try {
            const session = this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey);
            const response = await this.postAndReceiveMessage(userInput, session?.chatId);
            if (response.chatId !== undefined) {
                this.sessionStorage.save(this.chatSessionStorageKey, {
                    chatId: response.chatId,
                    assistantMessageId: response.assistantMessageId ?? session?.assistantMessageId
                });
            }
            return response;
        } catch (_) {
            return {
                message: this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE"),
                responseType: "internal_server_error"
            };
        }
    }

    private saveMessage(message: Message): Message[] {
        const stored = this.sessionStorage.fetch<Message[]>(this.chatCommunicationStorageKey) ?? [];
        const messages = [...stored, message];
        this.sessionStorage.save(this.chatCommunicationStorageKey, messages);
        return messages;
    }

    private async postAndReceiveMessage(userMessage: string, chatId: string | undefined): Promise<ChatResponse> {
        return await schedule(this.gptChatService.postMessage(this.configId, userMessage, this.languages.activeLanguageId(), chatId)).as("post-message");
    }

    private postProcessMessage(message: string, responseType: string): string {
        if (responseType === "success") {
            return this.messageProcessor.process(message);
        } else if (responseType === "content_too_long") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_CONTENT_TOO_LONG");
        } else if (responseType === "content_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_CONTENT_VIOLATION");
        } else if (responseType === "bad_conduct_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_BAD_CONDUCT");
        } else if (responseType === "company_policy_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_COMPANY_POLICY");
        } else if (responseType === "internal_server_error") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE");
        } else {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE");
        }
    }

    public init(welcomeMessage: string): void {
        const oldMessages = this.sessionStorage.fetch<Message[]>(this.chatCommunicationStorageKey) ?? [];
        let messages: Message[];
        if (oldMessages.length === 0 && welcomeMessage !== "") {
            messages = this.saveMessage({content: welcomeMessage, role: Role.ASSISTANT});
            this.fireNewMessages(messages, MessageSource.WELCOME);
        } else {
            messages = oldMessages;
            this.fireNewMessages(messages, MessageSource.SESSION_STORAGE);
        }
    }

    private fireNewMessages(messages: Message[], source: MessageSource): void {
        this.newMessagesCallback.forEach(callback => callback(messages, source));
    }
}